/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Spin,
  Table,
  Tag,
  Tooltip,
  message
} from 'antd';
import {
  LoadingOutlined,
  ZoomOutOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  UserOutlined,
  UserSwitchOutlined,
  SyncOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table/interface';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import InstitutionUserService from '../../services/InstitutionUserService';

const antIcon = <LoadingOutlined style={{ fontSize: '24px', color: '#37762F' }} spin />;
const { RangePicker } = DatePicker;

interface DataType {
  id: string;
  table_source: string;
  request_type: string;
  bank_code: string;
  transaction_reference: string;
  debit_account_number: string;
  debit_account_name: string;
  beneficiary_account_name: string;
  beneficiary_bank_name: string;
  beneficiary_account_number: string;
  phone_number: string;
  email: string;
  transaction_currency: string;
  amount: number | string;
  reason: string;
  link: string | null;
  status: string;
  created_by: string;
  bank_reference: string | null;
  bailiff_assignee: string | null;
  bailiff_national_id: string | null;
  bailiff_phone_number: string | null;
  bailiff_email: string | null;
  property_id: string | null;
  execution_case_number: string | null;
  created_at: string;
  updated_at: string;
}

interface StatsType {
  bidderCount: number;
  bidderAmount: number;
  bailiffCount: number;
  bailiffAmount: number;
  totalCount: number;
  totalAmount: number;
  initiatedCount: number;
  initiatedAmount: number;
  failedCount: number;
  failedAmount: number;
}

const ViewSuccessRefundsReport: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [refundList, setRefundList] = useState<DataType[]>([]);
  const [searchText, setSearchText] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [stats, setStats] = useState<StatsType>({
    bidderCount: 0,
    bidderAmount: 0,
    bailiffCount: 0,
    bailiffAmount: 0,
    totalCount: 0,
    totalAmount: 0,
    initiatedCount: 0,
    initiatedAmount: 0,
    failedCount: 0,
    failedAmount: 0
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const [sortField, setSortField] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('DESC');

  const fetchSuccessRefunds = async () => {
    setLoading(true);
    setStatsLoading(true);
    try {
      const response = await InstitutionUserService.getSuccessRefundsReport({
        limit: pagination.pageSize,
        page: pagination.current,
        search: searchText || undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        sort_by: sortField,
        sort_order: sortOrder
      });

      if (response && response.status === 200) {
        setRefundList(response.data || []);
        const meta = response.meta || {};
        setPagination(prev => ({
          ...prev,
          total: meta.total || 0
        }));
        if (meta.stats) {
          setStats(meta.stats);
        }
      }
    } catch (error) {
      console.error('Error fetching successful refunds report:', error);
      message.error('Failed to load successful refunds report.');
    } finally {
      setLoading(false);
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchSuccessRefunds();
  }, [pagination.current, pagination.pageSize, searchText, fromDate, toDate, sortField, sortOrder]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const clearFilters = () => {
    form.resetFields();
    setSearchText('');
    setFromDate('');
    setToDate('');
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));

    if (sorter && sorter.field) {
      setSortField(sorter.field);
      setSortOrder(sorter.order === 'ascend' ? 'ASC' : 'DESC');
    }
  };

  const renderStatCard = (
    title: string,
    value: string | number,
    subtitle: string | number,
    icon: React.ReactNode,
    iconColor: string,
    iconBg: string
  ) => {
    return (
      <Card
        bordered={false}
        className="rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 w-full"
        style={{
          background: '#ffffff',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)',
          height: '115px',
          display: 'flex',
          alignItems: 'center',
          padding: '16px'
        }}
        bodyStyle={{ padding: 0, width: '100%' }}
      >
        <div className="flex items-center justify-between w-full">
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider m-0" style={{ color: '#64748b' }}>{title}</p>
            <h3 className="text-gray-800 text-lg sm:text-xl md:text-2xl font-bold mt-0.5 mb-0" style={{ color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {statsLoading ? <Spin size="small" indicator={<LoadingOutlined style={{ fontSize: 18, color: '#37762F' }} spin />} /> : value}
            </h3>
            {subtitle !== undefined && subtitle !== null && subtitle !== '' && (
              <p className="text-xs font-semibold m-0 mt-0.5" style={{ color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {statsLoading ? '' : subtitle}
              </p>
            )}
          </div>
          <div
            className="text-2xl flex items-center justify-center rounded-lg p-3 flex-shrink-0"
            style={{
              color: iconColor,
              backgroundColor: iconBg,
              width: '46px',
              height: '46px',
              marginLeft: '12px'
            }}
          >
            {icon}
          </div>
        </div>
      </Card>
    );
  };

  const columns: ColumnsType<DataType> = [
    {
      title: 'Request Type',
      dataIndex: 'request_type',
      key: 'request_type',
      width: 120,
      render: (type: string) => {
        const isBailiff = type === 'bailiff';
        return (
          <Tag
            color={isBailiff ? 'blue' : 'green'}
            style={{
              borderRadius: '6px',
              fontWeight: '600',
              padding: '2px 8px',
              textTransform: 'uppercase',
              fontSize: '10px'
            }}
          >
            {isBailiff ? 'Bailiff' : 'Bidder'}
          </Tag>
        );
      }
    },
    {
      title: 'Transaction Reference',
      dataIndex: 'transaction_reference',
      key: 'transaction_reference',
      width: 180,
      render: (ref: string) => (
        <span className="font-semibold text-gray-800" style={{ fontFamily: 'monospace' }}>
          {ref}
        </span>
      )
    },
    {
      title: 'Bank Reference',
      dataIndex: 'bank_reference',
      key: 'bank_reference',
      width: 180,
      render: (ref: string) => (
        <span className="font-semibold text-blue-600" style={{ fontFamily: 'monospace' }}>
          {ref || '—'}
        </span>
      )
    },
    {
      title: 'Beneficiary Name',
      dataIndex: 'beneficiary_account_name',
      key: 'beneficiary_account_name',
      ellipsis: true,
      render: (name: string, record: DataType) => {
        const displayName = name || record.bailiff_assignee || 'N/A';
        return <span className="font-medium text-gray-700">{displayName}</span>;
      }
    },
    {
      title: 'Bank Name',
      dataIndex: 'beneficiary_bank_name',
      key: 'beneficiary_bank_name',
      width: 150,
      render: (bank: string, record: DataType) => (
        <span>{bank || record.bank_code || 'N/A'}</span>
      )
    },
    {
      title: 'Account Number',
      dataIndex: 'beneficiary_account_number',
      key: 'beneficiary_account_number',
      width: 160,
      render: (acc: string, record: DataType) => (
        <span className="text-gray-600" style={{ fontFamily: 'monospace' }}>
          {acc || record.debit_account_number || 'N/A'}
        </span>
      )
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      align: 'right' as const,
      sorter: true,
      render: (amt: number | string) => {
        const val = parseInt(String(amt || '0'));
        return (
          <span className="font-bold text-gray-900">
            {val.toLocaleString()} RWF
          </span>
        );
      }
    },
    {
      title: 'Refund Date',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 160,
      sorter: true,
      render: (date: string) => (
        <span className="text-gray-600">
          {moment(date).format('YYYY-MM-DD HH:mm')}
        </span>
      )
    },
    {
      title: 'Reason / Case',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      render: (reason: string, record: DataType) => {
        const details = record.execution_case_number
          ? `Case: ${record.execution_case_number} | Prop: ${record.property_id || ''}`
          : reason || 'Approved successful refund';
        return (
          <Tooltip title={details}>
            <span className="text-gray-500 text-xs">{details}</span>
          </Tooltip>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      filters: [
        { text: 'Pending', value: 'PENDING' },
        { text: 'Initiated', value: 'INITIATED' },
        { text: 'Completed', value: 'COMPLETED' }
      ],
      onFilter: (value, record) => {
        if (value === 'COMPLETED') {
          return record.status === 'COMPLETED' || record.status === 'APPROVED';
        }
        return record.status === value;
      },
      render: (status: string) => {
        type StatusConfig = {
          color: string;
          icon: React.ReactNode;
          label: string;
          description: string;
          bg: string;
          border: string;
          textColor: string;
        };
        const map: Record<string, StatusConfig> = {
          PENDING: {
            color: 'warning',
            icon: <ClockCircleOutlined />,
            label: 'Pending',
            description: 'Awaiting approval',
            bg: '#fffbeb',
            border: '#fde68a',
            textColor: '#b45309'
          },
          INITIATED: {
            color: 'processing',
            icon: <SyncOutlined spin />,
            label: 'Initiated',
            description: 'Transfer in progress',
            bg: '#eff6ff',
            border: '#bfdbfe',
            textColor: '#1d4ed8'
          },
          COMPLETED: {
            color: 'success',
            icon: <CheckCircleOutlined />,
            label: 'Completed',
            description: 'Payment completed',
            bg: '#f0fdf4',
            border: '#bbf7d0',
            textColor: '#15803d'
          },
          APPROVED: {
            color: 'success',
            icon: <CheckCircleOutlined />,
            label: 'Completed',
            description: 'Payment completed',
            bg: '#f0fdf4',
            border: '#bbf7d0',
            textColor: '#15803d'
          },
          FAILED: {
            color: 'error',
            icon: <CloseCircleOutlined />,
            label: 'Bank Transfer Failed',
            description: 'Refund failed at BK Bank',
            bg: '#fef2f2',
            border: '#fecaca',
            textColor: '#b91c1c'
          },
          "Bank Transfer Failed": {
            color: 'error',
            icon: <CloseCircleOutlined />,
            label: 'Bank Transfer Failed',
            description: 'Refund failed at BK Bank',
            bg: '#fef2f2',
            border: '#fecaca',
            textColor: '#b91c1c'
          }
        };
        const cfg = map[status] ?? {
          color: 'default',
          icon: null,
          label: status,
          description: '',
          bg: '#f8fafc',
          border: '#e2e8f0',
          textColor: '#475569'
        };
        return (
          <Tooltip title={cfg.description}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              borderRadius: '20px',
              background: cfg.bg,
              border: `1px solid ${cfg.border}`,
              color: cfg.textColor,
              fontWeight: 600,
              fontSize: '12px',
              whiteSpace: 'nowrap'
            }}>
              {cfg.icon}
              {cfg.label}
            </div>
          </Tooltip>
        );
      }
    }
  ];

  return (
    <div className="w-full p-6 bg-gray-50/50 min-h-screen">
      {/* Title Banner */}
      <div className="mb-6 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3.5 bg-emerald-50 rounded-xl text-emerald-600 flex items-center justify-center" style={{
              background: '#ecfdf5',
              color: '#059669',
              boxShadow: '0 4px 8px rgba(5, 150, 105, 0.15)',
            }}>
              <span style={{ fontSize: '20px' }}>💰</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800 m-0">Refunds Report</h2>
              <p className="text-gray-500 m-0 text-sm font-medium">Unified overview of all processed bidder and bailiff refunds</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Summary Section */}
      <Row gutter={[16, 16]} className="mb-6" style={{ display: 'flex', flexWrap: 'wrap' }}>
        <Col style={{ flex: '1 1 220px' }}>
          {renderStatCard(
            "Total Refunded Amount",
            `${stats.totalAmount.toLocaleString()} RWF`,
            "Bidders & Bailiffs",
            <DollarOutlined />,
            "#059669",
            "#d1fae5"
          )}
        </Col>
        <Col style={{ flex: '1 1 220px' }}>
          {renderStatCard(
            "Initiated Refunds",
            `${stats.initiatedCount || 0} Refunds`,
            `${(stats.initiatedAmount || 0).toLocaleString()} RWF`,
            <SyncOutlined />,
            "#2563eb",
            "#dbeafe"
          )}
        </Col>
        <Col style={{ flex: '1 1 220px' }}>
          {renderStatCard(
            "Failed Refunds",
            `${stats.failedCount || 0} Refunds`,
            `${(stats.failedAmount || 0).toLocaleString()} RWF`,
            <CloseCircleOutlined />,
            "#ef4444",
            "#fee2e2"
          )}
        </Col>
        <Col style={{ flex: '1 1 220px' }}>
          {renderStatCard(
            "Bidders Refunded",
            `${stats.bidderCount} Refunds`,
            `${stats.bidderAmount.toLocaleString()} RWF`,
            <UserOutlined />,
            "#4f46e5",
            "#e0e7ff"
          )}
        </Col>
        <Col style={{ flex: '1 1 220px' }}>
          {renderStatCard(
            "Bailiffs Refunded",
            `${stats.bailiffCount} Refunds`,
            `${stats.bailiffAmount.toLocaleString()} RWF`,
            <UserSwitchOutlined />,
            "#d97706",
            "#fef3c7"
          )}
        </Col>
        <Col style={{ flex: '1 1 220px' }}>
          {renderStatCard(
            "Total Success Transactions",
            `${stats.totalCount} Payments`,
            "Successful BK transfers",
            <FileTextOutlined />,
            "#6b7280",
            "#f3f4f6"
          )}
        </Col>
      </Row>

      {/* Filters Card */}
      <Card className="mb-6 rounded-xl border border-gray-200 shadow-sm">
        <Form form={form} layout="vertical">
          <Row gutter={[16, 16]} align="bottom">
            <Col xs={24} md={12} xl={8}>
              <Form.Item name="search" label={<span className="font-semibold text-gray-700">Quick Search</span>} style={{ marginBottom: 0 }}>
                <Input
                  size="large"
                  placeholder="Search by assignee, case, reference, or bank..."
                  prefix={<ZoomOutOutlined style={{ color: '#6b7280' }} />}
                  onChange={handleSearch}
                  value={searchText}
                  style={{ borderRadius: '8px' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={10}>
              <Form.Item name="range_picker" label={<span className="font-semibold text-gray-700">Select Date Range</span>} style={{ marginBottom: 0 }}>
                <RangePicker
                  size="large"
                  className="w-full"
                  allowClear
                  style={{ borderRadius: '8px' }}
                  onChange={(dates, dateStrings) => {
                    if (dates) {
                      setFromDate(dates[0] ? dates[0].startOf('day').toISOString() : '');
                      setToDate(dates[1] ? dates[1].endOf('day').toISOString() : '');
                    } else {
                      setFromDate('');
                      setToDate('');
                    }
                    setPagination(prev => ({ ...prev, current: 1 }));
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} xl={6}>
              <Button
                onClick={clearFilters}
                className="w-full enhanced-button"
                size="large"
                style={{ borderRadius: '8px' }}
              >
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Success Table */}
      <Card className="rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <Spin spinning={loading} indicator={antIcon} tip="Loading success refunds report...">
          <Table
            rowKey="id"
            columns={columns}
            dataSource={refundList}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} success refunds`
            }}
            onChange={handleTableChange}
            scroll={{ x: 800 }}
            rowClassName={(record, index) => (index % 2 === 0 ? 'bg-white' : 'bg-gray-50')}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default ViewSuccessRefundsReport;
